import { useEffect, useState, useRef } from "react";
import dayjs from "dayjs";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { exportElementToPdf } from "@/lib/exportPdf";
import { useMetrics } from "@/context/MetricsContext";
import { metricColor, metricAverage, dayAverage, scoreColor, scoreTint, scoreBorder } from "@/lib/grades";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tooltip as UITooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { ChevronLeft, ChevronRight, StickyNote, Pill, Stethoscope, Download } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid,
} from "recharts";

export function MonthlyView({ patientId }) {
  const metrics = useMetrics();
  const [month, setMonth] = useState(dayjs().startOf("month"));
  const [entries, setEntries] = useState([]);
  const reportRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (!reportRef.current) return;
    setExporting(true);
    try {
      await exportElementToPdf(reportRef.current, `AnnieHealth-Monthly-${month.format("MMMM-YYYY")}.pdf`);
    } catch (err) {
      toast.error("Could not generate PDF");
    } finally {
      setExporting(false);
    }
  };

  const start = month.format("YYYY-MM-DD");
  const end = month.endOf("month").format("YYYY-MM-DD");

  useEffect(() => {
    const params = { start, end };
    if (patientId) params.patient_id = patientId;
    api.get("/entries", { params }).then((r) => setEntries(r.data)).catch(() => setEntries([]));
  }, [start, end, patientId]);

  const byDate = Object.fromEntries(entries.map((e) => [e.date, e]));
  const daysInMonth = month.daysInMonth();

  const trend = Array.from({ length: daysInMonth }, (_, i) => {
    const d = month.date(i + 1);
    const e = byDate[d.format("YYYY-MM-DD")];
    const row = { day: i + 1 };
    metrics.forEach((m) => (row[m.key] = e?.metrics?.[m.key] ?? null));
    return row;
  });

  const firstWeekday = month.day();
  const cells = [...Array(firstWeekday).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => month.date(i + 1))];

  return (
    <div ref={reportRef} data-testid="monthly-view" className="animate-fade-up space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="text-xl sm:text-2xl font-serif">{month.format("MMMM YYYY")}</h3>
        <div className="flex gap-2 no-export">
          <Button variant="outline" className="rounded-full" data-testid="monthly-export-pdf"
            onClick={handleExport} disabled={exporting}>
            <Download className="h-4 w-4 mr-1.5" /> {exporting ? "Exporting…" : "Export PDF"}
          </Button>
          <Button variant="outline" size="icon" className="rounded-full" data-testid="monthly-prev"
            onClick={() => setMonth(month.subtract(1, "month"))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" className="rounded-full" data-testid="monthly-next"
            onClick={() => setMonth(month.add(1, "month"))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Per-metric averages + count */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="monthly-averages">
        {metrics.map((m) => {
          const a = metricAverage(entries, m.key);
          return (
            <Card key={m.key} className="p-6 rounded-2xl border-border/70 flex flex-col items-center text-center">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{m.label}</div>
              <div className="text-5xl font-serif font-bold"
                style={{ color: a != null ? scoreColor(a) : undefined }} data-testid={`monthly-avg-${m.key}`}>
                {a != null ? a.toFixed(1) : "–"}
              </div>
            </Card>
          );
        })}
        <Card className="p-6 rounded-2xl border-border/70 flex flex-col items-center text-center">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Entries logged</div>
          <div className="text-5xl font-serif font-bold" data-testid="monthly-entry-count">{entries.length}</div>
        </Card>
      </div>

      {/* Trend chart */}
      <Card className="p-5 sm:p-6 rounded-2xl border-border/70">
        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-4">Progress trend (1–10)</div>
        <div style={{ width: "100%", height: 260 }} data-testid="monthly-trend-chart">
          <ResponsiveContainer>
            <LineChart data={trend} margin={{ top: 5, right: 10, left: -22, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis domain={[1, 10]} ticks={[2, 4, 6, 8, 10]} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))" }} />
              <Legend />
              {metrics.map((m, idx) => (
                <Line key={m.key} type="monotone" dataKey={m.key} name={m.label}
                  stroke={metricColor(m.key, idx)} strokeWidth={2.5} connectNulls
                  dot={{ r: 2.5 }} activeDot={{ r: 5 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Calendar */}
      <Card className="p-5 sm:p-6 rounded-2xl border-border/70">
        <div className="grid grid-cols-7 gap-1 mb-2 text-center">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="text-[10px] uppercase tracking-wider text-muted-foreground">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1 sm:gap-2" data-testid="monthly-calendar">
          {cells.map((d, i) => {
            if (!d) return <div key={`b${i}`} />;
            const key = d.format("YYYY-MM-DD");
            const e = byDate[key];
            const avg = dayAverage(e);
            const tip = e
              ? metrics.map((m) => `${m.short}: ${e.metrics?.[m.key] ?? "–"}`).join("  ")
              : "";
            const cell = (
              <div key={key} data-testid={`monthly-cell-${key}`} title={tip}
                className="relative aspect-square rounded-lg flex flex-col items-center justify-center border"
                style={{ backgroundColor: scoreTint(avg), borderColor: scoreBorder(avg) }}>
                {e?.note && (
                  <StickyNote className="absolute top-1 right-1 h-4 w-4 text-primary"
                    data-testid={`monthly-note-icon-${key}`} />
                )}
                <span className="absolute top-1 left-1.5 text-sm sm:text-base font-semibold text-muted-foreground leading-none">{d.format("D")}</span>
                {avg != null && (
                  <span className="font-mono font-bold text-2xl sm:text-3xl leading-tight" style={{ color: scoreColor(avg) }}>
                    {avg.toFixed(1)}
                  </span>
                )}
                {(e?.meds_taken || e?.doctor_visit) && (
                  <div className="absolute bottom-1 left-0 right-0 flex justify-center gap-1">
                    {e?.meds_taken && <Pill className="h-3.5 w-3.5 text-primary" />}
                    {e?.doctor_visit && <Stethoscope className="h-3.5 w-3.5 text-primary" />}
                  </div>
                )}
              </div>
            );
            if (!e?.note) return cell;
            return (
              <TooltipProvider key={key} delayDuration={100}>
                <UITooltip>
                  <TooltipTrigger asChild>{cell}</TooltipTrigger>
                  <TooltipContent className="max-w-[260px] whitespace-pre-wrap text-left"
                    data-testid={`monthly-note-tooltip-${key}`}>
                    {e.note}
                  </TooltipContent>
                </UITooltip>
              </TooltipProvider>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
